/**
 * Visual tone; each maps to token-based styles in styles.css. Never the only signal: always icon + label. Red
 * (`danger`) is for SOS and emergencies only (brand rule): the app SOS channel and `critical` severity. Every other
 * status (out of service, revoked, lapsed, refused, dismissed...) uses `warning` (orange) or a neutral tone (TRN RW8).
 */
export type Tone = "success" | "warning" | "danger" | "neutral" | "muted" | "inverse";

export interface StatusStyle {
  label: string;
  icon: string;
  tone: Tone;
}

type Styles = Readonly<Record<string, StatusStyle>>;
type Words = Readonly<Record<string, string>>;

/** The styles allowed to use red (SOS and emergencies); tests/contract-sync.test.ts checks nothing else does. */
export const RED_ALLOWED: ReadonlyArray<readonly [string, string]> = [
  ["SEVERITY", "critical"],
  ["CHANNEL", "app_sos"],
];

// tests/contract-sync.test.ts checks that each table covers its contract enum exactly.

export const SEVERITY: Styles = {
  low: { label: "Low", icon: "▁", tone: "neutral" },
  medium: { label: "Medium", icon: "▃", tone: "neutral" },
  high: { label: "High", icon: "▅", tone: "warning" },
  critical: { label: "Critical", icon: "▇", tone: "danger" },
};
export const SEVERITY_ORDER = ["low", "medium", "high", "critical"] as const;

export const CHANNEL: Styles = {
  app_sos: { label: "SOS (app)", icon: "SOS", tone: "danger" },
  phone: { label: "Phone call", icon: "☎", tone: "neutral" },
  sms: { label: "SMS", icon: "✉", tone: "neutral" },
  whatsapp: { label: "WhatsApp", icon: "✉", tone: "neutral" },
  ussd: { label: "USSD relay", icon: "#", tone: "neutral" },
  walk_in: { label: "Walk-in", icon: "⌂", tone: "neutral" },
  radio: { label: "Radio", icon: "⌁", tone: "neutral" },
  aml: { label: "Network location", icon: "◎", tone: "muted" },
};
/** Channels a call-taker types (`createAlert`). */
export const TYPED_CHANNELS = ["phone", "ussd", "radio", "walk_in"] as const;

export const ALERT_STATUS: Styles = {
  new: { label: "New – to handle", icon: "●", tone: "warning" },
  confirmed: { label: "Confirmed (new incident)", icon: "✓", tone: "success" },
  linked: { label: "Linked to an incident", icon: "↪", tone: "neutral" },
  dismissed: { label: "Dismissed", icon: "✕", tone: "muted" },
};

export const ALERT_FLAG: Styles = {
  repeat_caller: { label: "Repeat caller", icon: "↻", tone: "warning" },
  no_callback_number: { label: "No number to call back", icon: "☎̸", tone: "warning" },
  outside_service_area: { label: "Outside the service area", icon: "⌖", tone: "warning" },
  location_pending: { label: "Location pending", icon: "?", tone: "warning" },
  stale_token: { label: "Sent with an expired sign-in", icon: "⏱", tone: "muted" },
  reopened: { label: "Reopened", icon: "↺", tone: "warning" },
};

export const LOCATION_SOURCE: Words = {
  device_gps: "Phone GPS",
  location_link: "Location link",
  message_pin: "Pin in a message",
  map_pin: "Pin dropped on the map",
  geocoded: "Place from the gazetteer",
  ward_landmark: "Landmark from the gazetteer",
};

export const ACCURACY_CLASS: Styles = {
  high: { label: "High accuracy", icon: "◉", tone: "success" },
  medium: { label: "Medium accuracy", icon: "◎", tone: "neutral" },
  low: { label: "Low accuracy", icon: "○", tone: "warning" },
};

export const PLACE_KIND: Words = { landmark: "Landmark", street: "Street", plot: "Plot", ward: "Ward" };

export const INCIDENT_STATUS: Styles = {
  open: { label: "Open – not dispatched", icon: "●", tone: "warning" },
  dispatched: { label: "Crew dispatched", icon: "➤", tone: "neutral" },
  on_scene: { label: "On scene", icon: "◆", tone: "neutral" },
  contained: { label: "Contained", icon: "◇", tone: "success" },
  cleared: { label: "Cleared", icon: "✓", tone: "success" },
  closed: { label: "Closed", icon: "■", tone: "inverse" },
  cancelled: { label: "Cancelled", icon: "✕", tone: "muted" },
};
/** Every status but `closed` and `cancelled` (the default of `listIncidents`). */
export const ACTIVE_INCIDENT_STATUSES = ["open", "dispatched", "on_scene", "contained", "cleared"] as const;

export const REPORT_STATUS: Styles = {
  none: { label: "No report", icon: "—", tone: "muted" },
  draft: { label: "Draft", icon: "✎", tone: "muted" },
  submitted: { label: "Submitted – to approve", icon: "?", tone: "warning" },
  returned: { label: "Returned to its authors", icon: "↩", tone: "warning" },
  approved: { label: "Approved", icon: "✓", tone: "success" },
};

export const AUTH_KIND: Words = { realm: "Normal sign-in", local_auth: "FER local sign-in" };

export const CANCELLATION_REASON: Words = {
  created_in_error: "Created in error",
  duplicate_incident: "Duplicate of another incident",
  resolved_before_arrival: "Resolved before arrival",
};

export const DISPATCH_STATUS: Styles = {
  assigned: { label: "Assigned – waiting to accept", icon: "…", tone: "warning" },
  accepted: { label: "Accepted", icon: "✓", tone: "neutral" },
  en_route: { label: "En route", icon: "➤", tone: "neutral" },
  on_scene: { label: "On scene", icon: "◆", tone: "neutral" },
  returning: { label: "Returning", icon: "↩", tone: "muted" },
  released: { label: "Released", icon: "■", tone: "success" },
  declined: { label: "Declined", icon: "✕", tone: "warning" },
  stood_down: { label: "Stood down", icon: "⏹", tone: "muted" },
};
export const DISPATCH_FINAL = ["released", "declined", "stood_down"] as const;
/** Statuses the dispatcher records from the radio (`recordDispatchStatus`). */
export const RADIO_STATUSES = ["accepted", "en_route", "on_scene", "returning", "released"] as const;

export const MILESTONE_SOURCE: Words = { app: "Work app", radio: "Radio (dispatcher)", console: "Console", system: "System" };

export const DECLINE_REASON: Words = {
  appliance_out_of_service: "Appliance out of service",
  committed_elsewhere: "Committed elsewhere",
  crew_unavailable: "Crew unavailable",
  other: "Other",
};

export const MESSAGE_STATUS: Styles = {
  queued: { label: "Queued", icon: "…", tone: "muted" },
  accepted: { label: "Accepted", icon: "→", tone: "neutral" },
  sent: { label: "Sent", icon: "✓", tone: "neutral" },
  delivered: { label: "Delivered", icon: "✓✓", tone: "success" },
  failed: { label: "Failed – not sent", icon: "!", tone: "warning" },
};
export const MESSAGE_PATH: Words = { notify: "Council messaging", direct_sms: "FER's own SMS gateway" };
export const MESSAGE_CHANNEL: Words = { push: "Push", sms: "SMS" };
export const MESSAGE_RECIPIENT: Words = { caller: "Caller", crew_commander: "Crew commander" };

export const TIMELINE_KIND: Words = {
  alert_received: "Alert received",
  alert_linked: "Alert linked",
  alert_reopened: "Alert reopened and unlinked",
  incident_created: "Incident created",
  type_changed: "Type changed",
  severity_changed: "Severity changed",
  location_changed: "Location changed",
  fix_received: "Better position received",
  dispatch_assigned: "Appliance assigned",
  dispatch_status: "Crew status",
  dispatch_declined: "Dispatch declined",
  dispatch_stood_down: "Dispatch stood down",
  dispatch_moved_in: "Dispatch moved in",
  dispatch_moved_out: "Dispatch moved out",
  backup_requested: "Backup requested",
  backup_acknowledged: "Backup request acknowledged",
  contained: "Contained",
  cleared: "Cleared",
  report_submitted: "Report submitted",
  report_returned: "Report returned",
  report_approved: "Report approved",
  closed: "Closed",
  cancellation_proposed: "Cancellation proposed",
  cancellation_rejected: "Cancellation rejected",
  cancelled: "Cancelled",
  note: "Note",
};
export const TIMELINE_SOURCE: Words = { console: "Console", app: "Work app", radio: "Radio", system: "System", channel: "Message channel" };

export const CALLBACK_OUTCOME: Words = {
  answered: "Answered",
  no_answer: "No answer",
  busy: "Busy",
  unreachable: "Unreachable",
  wrong_number: "Wrong number",
  no_number: "No number to call",
};

export const ASSIGN_OUTCOME: Styles = {
  assigned: { label: "Assigned", icon: "✓", tone: "success" },
  existing: { label: "Already dispatched here", icon: "=", tone: "neutral" },
  refused: { label: "Not assigned", icon: "!", tone: "warning" },
};
export const ASSIGN_REFUSAL: Words = {
  off_duty: "Nobody is booked on (off duty)",
  out_of_service: "Out of service",
  dispatched_elsewhere: "Dispatched to another incident",
  unknown_appliance: "Unknown or retired appliance",
};

export const ESTIMATE_METHOD: Words = {
  osrm: "Road route (OSRM)",
  straight_line: "Straight-line estimate (routing unavailable)",
};
export const SUGGESTION_ORIGIN: Words = { position: "From its last position", home_station: "From its home station" };

export const APPLIANCE_STATUS: Styles = {
  available: { label: "Available", icon: "●", tone: "success" },
  dispatched: { label: "Dispatched", icon: "➤", tone: "neutral" },
  out_of_service: { label: "Out of service", icon: "⚠", tone: "warning" },
  off_duty: { label: "Off duty (nobody booked on)", icon: "○", tone: "muted" },
};
export const BOOKING_END: Words = {
  booked_off: "Booked off",
  replaced: "Replaced by another commander",
  assignment_ended: "Assignment ended",
  role_revoked: "Role revoked",
  appliance_retired: "Appliance retired",
};
export const ASSIGNMENT_END: Words = { ended_by_admin: "Ended by a FER admin", role_revoked: "Role revoked" };

export const PREMISES_STATUS: Styles = {
  active: { label: "Active", icon: "✓", tone: "neutral" },
  closed: { label: "Closed", icon: "■", tone: "muted" },
};
export const PREMISES_CERTIFICATE: Styles = {
  none: { label: "No certificate", icon: "—", tone: "muted" },
  valid: { label: "Valid", icon: "✓", tone: "success" },
  expiring: { label: "Valid – expiring soon", icon: "⏳", tone: "warning" },
  expired: { label: "Expired", icon: "⌛", tone: "warning" },
  revoked: { label: "Revoked", icon: "✕", tone: "warning" },
};
export const OWNER_KIND: Words = { person: "Person", business: "Business" };
export const PROVENANCE_SOURCE: Words = {
  field_survey: "Field survey",
  paper_register: "Paper register",
  utility_import: "Water utility import",
  excel: "Spreadsheet",
  owner_application: "Owner's application",
  other: "Other",
};

export const APPLICATION_STATUS: Styles = {
  awaiting_payment: { label: "Awaiting payment", icon: "K", tone: "warning" },
  awaiting_inspection: { label: "Paid – awaiting inspection", icon: "⌕", tone: "neutral" },
  failed: { label: "Failed inspection", icon: "!", tone: "warning" },
  certified: { label: "Certified", icon: "✓", tone: "success" },
  lapsed: { label: "Lapsed (unpaid)", icon: "⌛", tone: "muted" },
  withdrawn: { label: "Withdrawn", icon: "↩", tone: "muted" },
  cancelled: { label: "Cancelled", icon: "✕", tone: "muted" },
  closed: { label: "Closed", icon: "■", tone: "muted" },
};
export const APPLICATION_KIND: Words = { new: "New", renewal: "Renewal" };
export const APPLIED_AS: Words = { owner: "The owner", fire_officer: "A fire officer for the owner" };
export const BILL_KIND: Words = {
  certificate_fee: "Certificate fee",
  reinspection_fee: "Re-inspection fee",
  reversal_debt: "Debt after a reversed payment",
};
export const BILL_STATUS: Styles = {
  pending: { label: "Not yet raised", icon: "…", tone: "muted" },
  issued: { label: "Issued – unpaid", icon: "K", tone: "warning" },
  payment_pending: { label: "Payment in progress", icon: "⏳", tone: "neutral" },
  paid: { label: "Paid", icon: "✓", tone: "success" },
  failed: { label: "Payment failed", icon: "!", tone: "warning" },
  cancelled: { label: "Cancelled", icon: "✕", tone: "muted" },
  expired: { label: "Expired", icon: "⌛", tone: "muted" },
  reversed: { label: "Payment reversed", icon: "↩", tone: "warning" },
};
/** Bill statuses that are open for payment (and so may be cancelled by four-eyes). */
export const OPEN_BILL_STATUSES = ["pending", "issued", "failed"] as const;
export const REINSPECTION_STATUS: Styles = {
  awaiting_payment: { label: "Awaiting payment", icon: "K", tone: "warning" },
  paid: { label: "Paid – awaiting inspection", icon: "⌕", tone: "neutral" },
  done: { label: "Inspected", icon: "✓", tone: "success" },
  lapsed: { label: "Lapsed (unpaid)", icon: "⌛", tone: "muted" },
  cancelled: { label: "Cancelled", icon: "✕", tone: "muted" },
};
export const BILL_CANCELLATION_REASON: Words = {
  applied_in_error: "Applied in error",
  duplicate_application: "Duplicate application",
  premises_not_liable: "Premises not liable",
  premises_closed: "Premises closed",
  owner_dissolved: "Owner dissolved",
  other: "Other",
};

export const INSPECTION_KIND: Words = { certificate: "Certificate inspection", routine: "Routine inspection" };
export const INSPECTION_OUTCOME: Styles = {
  pass: { label: "Pass", icon: "✓", tone: "success" },
  fail: { label: "Fail", icon: "!", tone: "warning" },
  advisory: { label: "Advisory", icon: "i", tone: "neutral" },
};
export const ANSWER_RESULT: Words = { pass: "Pass", fail: "Fail", not_applicable: "Not applicable" };
export const DEFECT_SEVERITY: Styles = {
  minor: { label: "Minor", icon: "▁", tone: "neutral" },
  major: { label: "Major", icon: "▅", tone: "warning" },
  critical: { label: "Critical defect", icon: "▇", tone: "warning" },
};

export const HELD_STATUS: Styles = {
  held_for_review: { label: "Held – to review", icon: "?", tone: "warning" },
  accepted: { label: "Accepted", icon: "✓", tone: "success" },
  rejected: { label: "Rejected", icon: "✕", tone: "muted" },
};
export const HELD_KIND: Words = { inspection: "Inspection", hydrant_register: "New hydrant", hydrant_survey: "Hydrant survey" };
export const HOLD_REASON: Words = { not_in_ward: "Captured outside the officer's wards at the time" };
export const FIELD_OUTCOME: Styles = {
  recorded: { label: "Recorded", icon: "✓", tone: "success" },
  duplicate: { label: "Already recorded", icon: "=", tone: "neutral" },
  held_for_review: { label: "Held for review", icon: "?", tone: "warning" },
  invalid_capture_time: { label: "Refused: capture time outside the window", icon: "⏱", tone: "warning" },
  invalid: { label: "Refused: not valid", icon: "!", tone: "warning" },
  rejected: { label: "Refused", icon: "✕", tone: "warning" },
  retry: { label: "Waiting to be sent again", icon: "↻", tone: "muted" },
};

export const CERTIFICATE_STATUS: Styles = {
  valid: { label: "Valid", icon: "✓", tone: "success" },
  expired: { label: "Expired", icon: "⌛", tone: "warning" },
  revoked: { label: "Revoked", icon: "✕", tone: "warning" },
};
export const REVOCATION_REASON: Words = {
  inspection_failed: "Inspection failed",
  false_information: "False information",
  change_of_use: "Change of use",
  premises_altered: "Premises altered",
  payment_reversed: "Payment reversed",
  court_order: "Court order",
  other: "Other",
};

export const HYDRANT_CONDITION: Styles = {
  working: { label: "Working", icon: "●", tone: "success" },
  defective: { label: "Defective", icon: "◐", tone: "warning" },
  out_of_service: { label: "Out of service", icon: "⚠", tone: "warning" },
  not_found: { label: "Not found", icon: "?", tone: "muted" },
};

export const PROPOSAL_KIND: Words = {
  incident_cancellation: "Incident cancellation",
  risk_class_change: "Risk-class change",
  occupancy_change: "Occupancy change",
  owner_change: "Owner change",
  bill_cancellation: "Bill cancellation",
  certificate_revocation: "Certificate revocation",
};
export const PROPOSAL_STATUS: Styles = {
  pending: { label: "Waiting for a decision", icon: "?", tone: "warning" },
  confirmed: { label: "Confirmed", icon: "✓", tone: "success" },
  rejected: { label: "Rejected", icon: "✕", tone: "muted" },
  withdrawn: { label: "Withdrawn", icon: "↩", tone: "muted" },
  expired: { label: "Expired", icon: "⌛", tone: "muted" },
  superseded: { label: "Superseded", icon: "⇢", tone: "muted" },
};

export const GROUP_BY: Words = {
  none: "No grouping (all incidents)",
  ward: "Ward",
  incident_type: "Incident type",
  station: "First-arriving station",
  month: "Month",
};

export const UNKNOWN_STATUS: StatusStyle = { label: "Unknown", icon: "?", tone: "muted" };

export function statusOf(map: Styles, key: string | null | undefined): StatusStyle {
  return (key && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined) ?? { ...UNKNOWN_STATUS, label: key ?? "Unknown" };
}

export function labelOf(map: Words, key: string | null | undefined): string {
  if (!key) return "—";
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : key;
}

export function optionsOf(map: Words | Styles): Array<readonly [string, string]> {
  return Object.entries(map).map(([k, v]) => [k, typeof v === "string" ? v : v.label] as const);
}
